import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FEEDBACK_CREATE, FEEDBACK_VIEW } from '../../../../constants/FeedbackConst';
import {
  RBAC_PERMISSION_LEVEL_FULL_ACCESS,
  RBAC_TYPE_CMS_FEEDBACK,
} from '../../../../constants/RoleConsts';
import DeletePopupModel from '../../../../shared/delete-popup/Delete-Popup';
import LinearProgressBar from '../../../../shared/progress-bar/Progress';
import SearchBar from '../../../../shared/search-bar/Search-Bar';
import { notificationManager } from '../../../../utils/NotificationUtils';
import { feedbackSelector, fetchAllFeedback, setFeedbacksSuccess } from '../../cmsSlice';
import FeedBackTable from './Feedback-Table';
import {
  activateFeedbackStatus,
  deactivateFeedbackStatus,
  deleteFeedbackDetails,
} from '../../../../utils/cmsUtils';
import useOutsideAlerter from '../../../../custom-hooks/useOutsideAlerter';
import { createDataSortMapping } from '../../../../utils/PropertyAPIUtils';
import MultipleDeleteConfirmationPopup from '../../../../shared/MultiActionPopUp/MultiDeleteConfirmationPopup';
import { deleteSelectedItems, updateSelectedItemsStatus } from '../../../../utils/ServicesAPIUtils';
import {
  getCheckedIds,
  updateCheckedStatus,
  updateSelectedModule,
  generateConfirmationMessage,
  selectedCheckList,
  ResetFilterList,
} from '../../../../utils/formUtils';
import MultipleSelectionAlert from '../../../../shared/MultiActionPopUp/MultipleSelectionAlert';
import { decryptLocalStorage } from '../../../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../utils/loaderSlice';

const FeedbackList = (props) => {
  const {
    onDrawerToggle,
    onViewToggle,
    hotelId,
    loader,
    permissions,
    page,
    rowsPerPage,
    onsetRowsPerPage,
    onsetPage,
  } = props;
  const feedbacks = useSelector(feedbackSelector);
  const [feedbackList, setFeedbackList] = useState([]);
  const [selectedValue, setSelectedValue] = useState({
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    anchorEl: null,
    feedback: {},
    modalType: '',
    type: '',
  });
  const dispatch = useDispatch();
  const [visible, setVisible] = useState(false);
  const wrapperRef = useRef(null);
  const prevHotelIdRef = useRef(hotelId);
  const [popUp, setPopUp] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dragDisabled = !!searchText || feedbackList?.length <= 1;
  const [actionMenuList, setActionMenuList] = useState([]);
  const [showMultipleDeleteConfirmation, setShowMultipleDeleteConfirmation] = useState(false);
  const [actionName, setActionName] = useState(false);
  const [showBulkActiveDeactiveConfirmation, setShowBulkActiveDeactiveConfirmation] =
    useState(false);
  const [showBulkKebabSelectionAlert, setShowBulkKebabSelectionAlert] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [showBulkSearchAlert, setShowBulkSearchAlert] = useState(false);
  const getCheckedFeedbackList = feedbackList?.filter((feed) => feed?.isChecked);

  const listOfFeedbackIds = getCheckedFeedbackList?.map((feed) => feed.id);
  const selectedFeedbackLength = getCheckedFeedbackList?.length;

  useEffect(() => {
    setFeedbackList((prevList) => {
      return feedbacks?.map((feed) => {
        const isChecked = selectedCheckList(prevList, feed.id);
        return {
          ...feed,
          isChecked,
        };
      });
    });
    setSearchText('');
  }, [feedbacks]);

  const handleSearchText = async (searchText) => {
    setSearchText(searchText);
    const checkedIdList = getCheckedIds(feedbackList, 'isChecked');
    const filteredRows = await feedbacks.filter((item) => {
      return item.pageTitle.toLowerCase().includes(searchText.toLowerCase());
    });
    const updatedCheckedList = updateCheckedStatus(filteredRows, checkedIdList);
    onsetPage(0);
    setFeedbackList(updatedCheckedList);
    if (searchText && (isAllSelected || selectedFeedbackLength > 0)) {
      setShowBulkSearchAlert(true);
    } else if (!searchText) {
      setShowBulkSearchAlert(false);
      setFeedbackList(ResetFilterList);
    }
  };

  useEffect(() => {
    updateSelectedModule(feedbackList, setActionMenuList, setIsAllSelected);
  }, [feedbackList]);

  useEffect(() => {
    if (loader || feedbackList?.length === 0) return;
    const totalItems = feedbackList?.length;
    const maxPageIndex = Math.max(Math.ceil(totalItems / rowsPerPage) - 1, 0);
    if (page > maxPageIndex) {
      onsetPage(maxPageIndex);
    }
  }, [feedbackList?.length, rowsPerPage, page, onsetPage]);

  useEffect(() => {
    if (prevHotelIdRef?.current !== hotelId) {
      onsetPage(0);
    }
    prevHotelIdRef.current = hotelId;
  }, [hotelId]);

  const onOpenDrawer = (event, type) => {
    if (selectedFeedbackLength) {
      setFeedbackList(ResetFilterList);
    }
    event.stopPropagation();
    onDrawerToggle(type, true, selectedValue.feedback);
    handleClose();
  };

  const handleClose = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      warning: false,
      feedback: {},
      modalType: '',
      type: '',
    });
  };

  const onClickRowViewDetails = (feedbackItem, e, isClassDisabled) => {
    if (isClassDisabled) return onViewToggle(FEEDBACK_VIEW, true, feedbackItem);
    if (e?.target?.tagName === 'TD') {
      onViewToggle(FEEDBACK_VIEW, true, feedbackItem);
    }
  };

  const onViewDetails = () => {
    onViewToggle(FEEDBACK_VIEW, true, selectedValue.feedback);
  };

  const handleCellMenuClick = (event, feedback) => {
    if (selectedFeedbackLength > 1) {
      setShowBulkKebabSelectionAlert(true);
    } else {
      setSelectedValue({
        id: feedback.id,
        name: feedback.pageTitle,
        title: '',
        description: '',
        status: false,
        warning: false,
        anchorEl: event.currentTarget,
        feedback,
        modalType: '',
        type: '',
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    onsetPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onsetRowsPerPage(parseInt(event.target.value, 10));
    onsetPage(0);
  };

  const onDeleteFeedback = async () => {
    try {
      const selectedLanguage = decryptLocalStorage('lang');
      const language = selectedLanguage !== 'en' ? selectedLanguage : '';
      await deleteFeedbackDetails(hotelId, selectedValue.id, language);
      dispatch(fetchAllFeedback(hotelId));
      notificationManager('success', `Feedback deleted successfully`);
      handleClose();
    } catch (e) {
      if (e.graphQLErrors) notificationManager('danger', e.graphQLErrors[0].message);
      else notificationManager('danger', `Failed to delete feedback`);
    }
    setIsAllSelected(false);
    setActionMenuList([]);
  };

  const onClickDeleteFeedback = async () => {
    setSelectedValue((prev) => ({
      ...prev,
      title: 'Are you sure you want to delete',
      description: 'This action cannot be reversed',
      status: true,
      warning: false,
      anchorEl: null,
      modalType: 'deleteModal',
    }));
  };

  const onActiveDeactive = async (e, isActive) => {
    try {
      const selectedLanguage = decryptLocalStorage('lang');
      const language = selectedLanguage !== 'en' ? selectedLanguage : '';
      e.preventDefault();
      isActive
        ? await deactivateFeedbackStatus(hotelId, selectedValue.id, language)
        : await activateFeedbackStatus(hotelId, selectedValue.id, language);
      dispatch(fetchAllFeedback(hotelId));
      notificationManager(
        'success',
        `Feedback ${isActive ? 'deactivated' : 'activated'} successfully`,
      );
      handleClose();
    } catch (e) {
      if (e.graphQLErrors) notificationManager('danger', e.graphQLErrors[0].message);
      else
        notificationManager('danger', `Failed to ${isActive ? 'deactivate' : 'activate'} feedback`);
    }
    setIsAllSelected(false);
    setActionMenuList([]);
  };

  useOutsideAlerter(wrapperRef, visible, setPopUp);

  const handleAction = (actionName) => {
    let selectedValue = feedbackList?.filter((feed) => feed?.isChecked);
    switch (actionName) {
      case 'Delete':
        if (selectedValue?.length > 0) {
          setShowMultipleDeleteConfirmation(true);
          setActionName('delete');
        }
        break;
      case 'Activate':
        if (selectedValue?.length > 0) {
          setShowBulkActiveDeactiveConfirmation(true);
          setActionName('activate');
          setIsActive(true);
        }
        break;
      case 'Deactivate':
        if (selectedValue?.length > 0) {
          setShowBulkActiveDeactiveConfirmation(true);
          setActionName('deactivate');
          setIsActive(false);
        }
        break;
      default:
        break;
    }
  };

  const handleSelectAll = (event) => {
    let selectedFeedbackList = [];
    let actionListName = [];
    const CheckedValue = event?.target?.checked;
    setIsAllSelected(CheckedValue);
    selectedFeedbackList = feedbackList?.map((feed) => {
      return {
        ...feed,
        isChecked: CheckedValue,
      };
    });

    const isActive = selectedFeedbackList?.some((restaurant) => restaurant?.isActive);
    const isInactive = selectedFeedbackList?.some((restaurant) => !restaurant.isActive);

    if (isActive && !actionListName?.includes('Deactivate')) {
      actionListName.push('Deactivate');
    }
    if (isInactive && !actionListName?.includes('Activate')) {
      actionListName.push('Activate');
    }
    setFeedbackList([...selectedFeedbackList]);

    const sortActionMenuList = actionListName.sort((a, b) => {
      const order = { Activate: 1, Deactivate: 2, Delete: 3 };
      return order[a] - order[b];
    });

    setActionMenuList(sortActionMenuList);

    if (!event?.target?.checked && !selectedFeedbackList?.some((item) => item?.isChecked)) {
      setIsAllSelected(false);
      setActionMenuList([]);
    }
  };

  const handleCheckboxChange = (id) => {
    let actionListName = [];
    let actionList = [];
    let index = feedbackList?.findIndex((feed) => feed.id === id);
    if (!feedbackList[index]?.isChecked) {
      feedbackList[index] = { ...feedbackList[index], isChecked: true };
    } else {
      feedbackList[index].isChecked = !feedbackList[index]?.isChecked;
    }
    setFeedbackList([...feedbackList]);

    actionList = feedbackList
      ?.filter((sInfo) => sInfo?.isChecked)
      .map((status) => status?.isActive);

    const checkedItems = feedbackList?.filter((feed) => feed?.isChecked);

    if (checkedItems.length === 0) {
      setActionMenuList([]);
      setIsAllSelected(false);
      return;
    }

    const isActiveSelected = actionList.includes(true);
    const isInactiveSelected = actionList.includes(false);

    if (isActiveSelected && isInactiveSelected) {
      actionListName.push('Activate', 'Deactivate');
    } else if (isActiveSelected) {
      actionListName.push('Deactivate');
    } else if (isInactiveSelected) {
      actionListName.push('Activate');
    }

    const isAllSelected = feedbackList?.every((feed) => feed?.isChecked);
    setIsAllSelected(isAllSelected);

    setActionMenuList(actionListName);
  };

  const onApiCallConfirmationNotification = generateConfirmationMessage(
    'Feedback',
    listOfFeedbackIds?.length,
    isActive,
  );

  const handleMultipleStatusChange = async () => {
    handleClose();
    setActionMenuList([]);
    try {
      dispatch(stepUpLoaderCount());
      const lang = decryptLocalStorage('lang');
      const selectedLang = lang !== 'en' ? lang : '';
      const moduleName = 'FEEDBACK';
      await updateSelectedItemsStatus(
        listOfFeedbackIds,
        hotelId,
        selectedLang,
        moduleName,
        '',
        isActive,
        searchText.length > 0 ? false : isAllSelected,
      );
      dispatch(fetchAllFeedback(hotelId));
      notificationManager('success', onApiCallConfirmationNotification);
      setSelectedValue({
        id: '',
        name: '',
        title: '',
        description: '',
        status: false,
        activate: false,
        brand: null,
      });
    } catch (e) {
      notificationManager(
        'danger',
        e.graphQLErrors ? e.graphQLErrors[0].message : 'The status of the feedback was not changed',
      );
    } finally {
      dispatch(stepDownLoaderCount());
    }
    setFeedbackList(ResetFilterList);
    setIsAllSelected(false);
  };

  const deleteMultipleFeedback = async () => {
    handleClose();
    try {
      dispatch(stepUpLoaderCount());
      const lang = decryptLocalStorage('lang');
      const selectedLang = lang !== 'en' ? lang : '';
      const moduleName = 'FEEDBACK';
      await deleteSelectedItems(
        listOfFeedbackIds,
        hotelId,
        selectedLang,
        moduleName,
        '',
        searchText.length > 0 ? false : isAllSelected,
      );
      dispatch(fetchAllFeedback(hotelId));
      notificationManager('success', 'Feedbacks deleted successfully');
      setSelectedValue({
        ...selectedValue,
        id: '',
        name: '',
        title: '',
        description: '',
        status: false,
        brand: null,
      });
    } catch (e) {
      notificationManager(
        'danger',
        e.graphQLErrors ? e.graphQLErrors[0].message : 'Failed to delete Feedback',
      );
    } finally {
      dispatch(stepDownLoaderCount());
    }
    setIsAllSelected(false);
    setActionMenuList([]);
  };

  const handleBulkSearchAlertClose = () => {
    setShowBulkSearchAlert(false);
    setSearchText('');
    setFeedbackList(
      feedbacks?.map((res) => ({
        ...res,
        isChecked: false,
      })),
    );
  };

  return (
    <div className='mt-4 mb-4 group-list-container'>
      <SearchBar
        type={FEEDBACK_CREATE}
        buttonName='Add Feedback'
        setSearchText={handleSearchText}
        onOpenDrawer={onOpenDrawer}
        displayAddOrEdit={
          permissions[RBAC_TYPE_CMS_FEEDBACK] === RBAC_PERMISSION_LEVEL_FULL_ACCESS
            ? 'show'
            : 'hide'
        }
        visible={visible}
        setVisible={setVisible}
        popUp={popUp}
        setPopUp={setPopUp}
        placeHolder={feedbackList}
        setPlaceHolder={setFeedbackList}
        globalPlaceHolder={feedbacks}
        setGlobalPlaceHolder={setFeedbacksSuccess}
        dispatch={dispatch}
        mappingApi={createDataSortMapping}
        displayText='Feedback'
        module='FEEDBACK'
        searchText={searchText}
        setSearch={setSearchText}
        actionMenuItems={actionMenuList ?? []}
        handleAction={handleAction ?? false}
      />
      <div ref={wrapperRef} className='row mx-2 d-flex mt-4'>
        {loader && (
          <div className='pt-4 col-md-12'>
            <LinearProgressBar />
          </div>
        )}
        <FeedBackTable
          page={page}
          rowsPerPage={rowsPerPage}
          onOpenDrawer={onOpenDrawer}
          onClickRowViewDetails={onClickRowViewDetails}
          onViewDetails={onViewDetails}
          selectedValue={selectedValue}
          handleClose={handleClose}
          handleCellMenuClick={handleCellMenuClick}
          list={feedbackList}
          setFeedbackList={setFeedbackList}
          permissions={permissions}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          onClickDelete={onClickDeleteFeedback}
          onActiveDeactive={onActiveDeactive}
          setVisible={setVisible}
          handleSelectAll={handleSelectAll}
          isAllSelected={isAllSelected}
          setIsAllSelected={setIsAllSelected}
          handleCheckboxChange={handleCheckboxChange}
          selectedFeedbackLength={selectedFeedbackLength}
          dragDisabled={dragDisabled}
        />
      </div>
      {selectedValue.status && selectedValue.modalType === 'deleteModal' && (
        <DeletePopupModel
          deletePopUp={selectedValue}
          onDelete={onDeleteFeedback}
          onDeleteCancel={handleClose}
        />
      )}
      {showMultipleDeleteConfirmation && (
        <MultipleDeleteConfirmationPopup
          title={`Are you sure you want to ${actionName} this selection?`}
          subTitle={`A selection of ${selectedFeedbackLength} ${
            selectedFeedbackLength > 1 ? 'feedbacks' : 'feedback'
          } will be ${actionName}d. This Action is irreversible.`}
          isOpen={showMultipleDeleteConfirmation}
          onClose={() => setShowMultipleDeleteConfirmation(false)}
          onConfirm={() => {
            deleteMultipleFeedback();
          }}
          leftButtonName={'Cancel'}
          rightButtonName={'Delete'}
        />
      )}

      {showBulkActiveDeactiveConfirmation && (
        <MultipleDeleteConfirmationPopup
          title={`Are you sure you want to ${actionName} this selection?`}
          subTitle={`A selection of ${selectedFeedbackLength}  ${
            selectedFeedbackLength > 1 ? 'feedbacks' : 'feedback'
          } will be ${actionName}d.`}
          isOpen={showBulkActiveDeactiveConfirmation}
          onClose={() => setShowBulkActiveDeactiveConfirmation(false)}
          onConfirm={() => {
            handleMultipleStatusChange();
          }}
          leftButtonName={'No'}
          rightButtonName={'Yes'}
        />
      )}
      {showBulkSearchAlert && (
        <MultipleSelectionAlert
          isOpen={showBulkSearchAlert}
          onClose={handleBulkSearchAlertClose}
          message="If you perform a search after making selections, the items you've selected will be lost."
          ButtonName='Cancel'
        />
      )}
      {showBulkKebabSelectionAlert && (
        <MultipleSelectionAlert
          isOpen={showBulkKebabSelectionAlert}
          onClose={() => setShowBulkKebabSelectionAlert(false)}
          message='Please select one item from the list that you want to edit.'
          centerButtonName='OK'
        />
      )}
    </div>
  );
};

export default FeedbackList;
